"use client";

import { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '@bi-studio/shared/hooks';
import { StepsForm } from '@/components/forms';
import {
    LoadingOverlay,
    useMantineColorScheme,
    Box, Center, rem
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useForm } from '@mantine/form';
import { AlertCircle } from 'lucide-react';
import { getForm, submitForm, getPublicForm, submitPublicForm } from '@bi-studio/shared/apis';
import { LoadingScreen, NotFoundState } from '@bi-studio/shared/components';
import type { Form } from '@bi-studio/shared/lib';

export default function FormStepsPage() {
    const { token, isLoading: isAuthLoading } = useAuth();
    const { id } = useParams<{ id: string }>();
    const [searchParams] = useSearchParams();

    const [form, setForm] = useState<Form | null>(null);
    const [loading, setLoading] = useState(true);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const { colorScheme, setColorScheme } = useMantineColorScheme();

    useEffect(() => {
        const theme = searchParams.get("theme");
        if (theme === "dark" || theme === "light" || theme === "auto") {
            setColorScheme(theme);
        }
    }, [searchParams, setColorScheme]);

    const mantineForm = useForm<Record<string, any>>({
        initialValues: {},
        validate: (values) => {
            if (!form) return {};
            const errors: Record<string, string> = {};
            form.fields.forEach((field) => {
                if (field.required) {
                    const value = values[field.name];
                    if (value === undefined || value === null || value === '' ||
                        (field.type === 'checkbox' && value === false) ||
                        (field.type === 'multiselect' && Array.isArray(value) && value.length === 0)) {
                        errors[field.name] = `${field.label} is required`;
                    }
                }
            });
            return errors;
        },
    });

    const loadFormData = useCallback(() => {
        if (!id || isAuthLoading) return;

        setLoading(true);

        const fetchPromise = token
            ? getForm(token, id)
            : getPublicForm(id);

        fetchPromise
            .then((fetchedForm) => {
                setForm(fetchedForm);
                const initialValues: Record<string, any> = {};
                fetchedForm.fields.forEach((field) => {
                    if (field.type === 'checkbox') {
                        initialValues[field.name] = false;
                    } else if (field.type === 'number' || field.type === 'date') {
                        initialValues[field.name] = null;
                    } else if (field.type === 'multiselect') {
                        initialValues[field.name] = [];
                    } else {
                        initialValues[field.name] = '';
                    }
                });
                mantineForm.setInitialValues(initialValues);
                mantineForm.setValues(initialValues);
            })
            .catch((err) => {
                console.error("Failed to load form:", err);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [id, token, isAuthLoading]);

    useEffect(() => {
        if (!form) return;

        const config = (form.config as Record<string, any>) || {};
        const bgImage = config.backgroundImage;

        if (bgImage) {
            setImageLoaded(false);
            const img = new globalThis.Image();
            img.src = bgImage;
            img.onload = () => setImageLoaded(true);
            img.onerror = () => setImageLoaded(true);
        } else {
            setImageLoaded(true);
        }
    }, [form]);

    useEffect(() => {
        if (!isAuthLoading) {
            loadFormData();
        }
    }, [id, token, loadFormData, isAuthLoading]);

    const handleSubmit = async (values: typeof mantineForm.values) => {
        if (!id || !form) return;

        const cleanedData = { ...values };
        form.fields.forEach(field => {
            if (cleanedData[field.name] === '') {
                cleanedData[field.name] = null;
            }
        });

        setSubmitting(true);
        try {
            if (token) {
                await submitForm(token, id, cleanedData);
            } else {
                await submitPublicForm(id, cleanedData);
            }
            notifications.show({
                title: 'Success',
                message: 'Form submitted successfully',
                color: 'green',
            });
        } catch (error) {
            console.error('Failed to submit form:', error);
            notifications.show({
                title: 'Error',
                message: error instanceof Error ? error.message : 'Failed to submit form',
                color: 'red',
            });
        } finally {
            setSubmitting(false);
        }
    };

    const isReady = !!form;
    const isPageLoading = loading || isAuthLoading;
    const isImageLoading = isReady && !!(form.config as any)?.backgroundImage && !imageLoaded;

    if (!isReady && isPageLoading) {
        return (
            <Center h="100vh">
                <LoadingScreen message="Loading form..." />
            </Center>
        );
    }

    if (!form) {
        return (
            <NotFoundState
                title="Form Unavailable"
                message="This form is either private, does not exist, or you don't have permission to view it."
                icon={<AlertCircle size={32} />}
                showAction={false}
            />
        );
    }

    const config = (form.config as Record<string, any>) || {};
    const backgroundStyle = {
        backgroundImage: config.backgroundImage ? `url(${config.backgroundImage})` : undefined,
        backgroundColor: config.backgroundColor || (colorScheme === 'dark' ? 'var(--mantine-color-dark-9)' : 'var(--mantine-color-gray-0)'),
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed',
    };

    return (
        <Box style={{ 
            height: '100vh', 
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            ...backgroundStyle
        }}>
            <LoadingOverlay visible={isImageLoading} overlayProps={{ blur: 0, opacity: 1, color: 'var(--mantine-color-body)' }} zIndex={100} />
            
            <Box style={{ 
                flex: 1, 
                overflow: 'auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: rem(12),
                [`@media (min-width: 640px)`]: {
                    padding: rem(24),
                }
            }}>
                <Box maw={rem(800)} w="100%">
                    <StepsForm
                        form={form}
                        mantineForm={mantineForm}
                        submitting={submitting}
                        onSubmit={handleSubmit}
                    />
                </Box>
            </Box>
        </Box>
    );
}
